/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentChildren } from 'preact';

import { Flex } from '#UNSAFE_Flex';
import { Skeleton } from '#UNSAFE_Skeleton';
import { classNames } from '../utils/UNSAFE_classNames';
import { menuSkeletonStyles } from './themes/MenuSkeletonStyles.css';
import { useEffect, useState } from 'preact/hooks';
import { LiveRegion } from '#UNSAFE_LiveRegion';
import { BundleType } from '../resources/nls/bundle';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { MenuItemRedwoodTheme } from './themes/redwood/MenuItemTheme';

type MenuSkeletonContainerProps = {
  /**
   * Allows to pass and access the properties from child elements
   **/
  children?: (index: number) => ComponentChildren;
};

// TODO: Would be good to share a skeleton container among multiple components. JET-73171
export function MenuSkeletonContainer({ children }: MenuSkeletonContainerProps) {
  const { baseTheme } = useComponentTheme(MenuItemRedwoodTheme);
  const containerClasses = classNames([baseTheme, menuSkeletonStyles.container]);
  return (
    <div oj-menu-skeleton="true" class={containerClasses} role="presentation">
      {[...Array(4)].map((_element, index) => children?.(index))}
    </div>
  );
}

export const MenuSkeleton = () => {
  useEffect(() => {
    setTimeout(() => {
      setIsVisible(true);
    }, timerValue);
  }, []);

  const [isVisible, setIsVisible] = useState(false);
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

  // We keep the flex container even in the first 50ms so dropdown keeps the same size since the beginning
  return (
    <>
      {isVisible && <LiveRegion>{translations.menu_stateLoading()}</LiveRegion>}
      <MenuSkeletonContainer>
        {() => {
          return (
            <Flex height="11x" align="center">
              {isVisible && <Skeleton height="4x" />}
            </Flex>
          );
        }}
      </MenuSkeletonContainer>
    </>
  );
};

/**
 * Allows to specify the time delay for rendering the component
 **/
const timerValue = 50;
